import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,    
  Label,
  Input,
  FormFeedback,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import CustomDatePicker from "../../../common/CustomDatePicker";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchTimesheetClient, fetchAttendanceEmployeeList, fetchTripmaster, saveTripmaster, deleteTripmaster } from "store/actions";
import { convertToInputDate, formatDate } from "common/dateFormat/formatDate";

const TripMaster = () => {
  document.title = "Trip Master | HRMS";

  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [authDetails, setAuthDetails] = useState(null);

  const { clientList, employees, tripmasterList, loading, success } = useSelector((state) => ({
    clientList: state.TimesheetTransactionApplyReducer?.clientList ?? [],
    employees: state.AttendanceLeaveManagReducer?.employees ?? [],
    tripmasterList: state.TripmasterReducer?.tripmasterList ?? [],
    loading: state.TripmasterReducer?.loading ?? false,
    success: state.TripmasterReducer?.success ?? false,
  }));

  const activeTripmaster = tripmasterList.filter(trip => trip.b_active === 1);

  const clientTypes = [
    { value: 1, label: "Existing Client"},
    { value: 2, label: "Presales"}
  ];

  const [clientName, setClientName] = useState("");

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    const create_by = authUserDetails[0].user_id;
    dispatch(fetchTimesheetClient(unitId, create_by));
    
    const formData = new FormData();
    formData.append('emp_id', 0);
    formData.append('leave_date', '');
    formData.append('unit_id', unitId);
    formData.append('create_by', create_by);
    dispatch(fetchAttendanceEmployeeList(formData));
    
    const tripFormData = new FormData();
    tripFormData.append('unit_id', unitId);
    tripFormData.append('create_by', create_by);
    dispatch(fetchTripmaster(tripFormData));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const tripFormData = new FormData();
      tripFormData.append('unit_id', authDetails?.unit_id);
      tripFormData.append('create_by', authDetails?.user_id);
      dispatch(fetchTripmaster(tripFormData));
      formik.resetForm();
      setClientName("");
      setIsEdit(false);
      setEditId(null);
    }
  }, [success]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      clientType: "",
      client: "",
      tripName: "",
      assignTo: [],
      fromDate: "",
      toDate: "",
    },
    validationSchema: Yup.object({
      clientType: Yup.object().required("Please select client type"),
      client: Yup.mixed().when('clientType', {
        is: (val) => val?.value !== 2,
        then: () => Yup.object().required("Please select client"),
        otherwise: () => Yup.mixed().notRequired(),
      }),
      tripName: Yup.string().required("Please enter trip name"),
      assignTo: Yup.array().min(1, "Please select at least one employee").required("Please select assign to"),
      fromDate: Yup.string().required("Please select from date"),
      toDate: Yup.string()
        .required("Please select to date")
        .test('is-greater', 'To date must be after from date', function(value) {
          const { fromDate } = this.parent;
          return !fromDate || !value || new Date(value) >= new Date(fromDate);
        }),
    }),
    onSubmit: (values, { resetForm }) => {
      if (values.clientType?.value === 2 && !clientName.trim()) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please enter client name for Presales",
        });
        return;
      }
      const formData = new FormData();
      formData.append('trip_id', editId || 0);
      formData.append('client_type_id', values.clientType.value);
      formData.append('client_id', values.clientType?.value === 2 ? 0 : values.client.value);
      formData.append('pre_sales', values.clientType?.value === 2 ? clientName : '');
      formData.append('trip_name', values.tripName);
      formData.append('assign_to', values.assignTo.map(emp => emp.value).join(','));
      formData.append('from_date', formatDate(values.fromDate));
      formData.append('to_date', formatDate(values.toDate));
      formData.append('unit_id', authDetails?.unit_id);
      formData.append('create_by', authDetails?.user_id);
      dispatch(saveTripmaster(formData));
    },
  });

  const handleClientTypeChange = (option) => {
    formik.setFieldValue("clientType", option);
    if (option?.value === 2) {
      formik.setFieldValue("client", "");
      setClientName("");
    } else {
      formik.setFieldValue("client", "");
      setClientName("");
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "All unsaved changes will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        formik.resetForm();
        setClientName("");
        setIsEdit(false);
        setEditId(null);
      }
    });
  };

  const handleDelete = (tripId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append('trip_id', tripId);
        formData.append('create_by', authDetails?.user_id);
        dispatch(deleteTripmaster(formData));
      }
    });
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setEditId(row.trip_id);
    
    const clientType = clientTypes.find(ct => ct.value === row.client_type_id);
    const assignedEmployees = row.assign_to.split(',').map(id => {
      const emp = employees.find(e => e.emp_id === parseInt(id));
      return emp ? { value: emp.emp_id, label: emp.emp_name } : null;
    }).filter(Boolean);

    formik.setValues({
      clientType: clientType || "",
      client: row.client_type_id === 2 ? "" : { value: row.client_id, label: row.client_name },
      tripName: row.trip_name,
      assignTo: assignedEmployees,
      fromDate: row.from_date ? new Date(convertToInputDate(row.from_date)) : "",
      toDate: row.to_date ? new Date(convertToInputDate(row.to_date)) : "",
    });
    
    if (row.client_type_id === 2) {
      setClientName(row.pre_sales || row.client_name);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }) => activeTripmaster.length - row.index,
        className: "serial-no",
      },
      { Header: "Client Type", accessor: "client_type" },
      { 
        Header: "Client", 
        accessor: "client_name",
        Cell: ({ row }) => row.original.client_name || row.original.pre_sales || "-"
      },
      { Header: "Trip Name", accessor: "trip_name" },
      { Header: "Assign To", accessor: "assign" },
      { Header: "From Date", accessor: "from_date" },
      { Header: "To Date", accessor: "to_date" },
      {
        Header: "Action",
        Cell: (cellProps) => {
          const { trip_id } = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-danger"
                onClick={() => handleDelete(trip_id)}
              >
                <i
                  className="mdi mdi-delete font-size-18"
                  id={`delete-tooltip-${cellProps.row.id}`}
                />
                <UncontrolledTooltip placement="top" target={`delete-tooltip-${cellProps.row.id}`}>
                  Delete
                </UncontrolledTooltip>
              </Link>

              <Link
                to="#"
                className="text-success"
                onClick={() => handleEdit(cellProps.row.original)}
              >
                <i className="bx bxs-edit font-size-18" id={`edit-tooltip-${cellProps.row.id}`} />
                <UncontrolledTooltip placement="top" target={`edit-tooltip-${cellProps.row.id}`}>
                  Edit
                </UncontrolledTooltip>
              </Link>
            </div>
          );
        },
      },
    ],
    [authDetails, activeTripmaster]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Setup" breadcrumbItem="Trip Master" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Client Type <span className="text-danger">*</span>
                          </Label>
                          <Select
                            name="clientType"
                            options={clientTypes}
                            value={formik.values.clientType}
                            onChange={handleClientTypeChange}
                            className={
                              formik.touched.clientType && formik.errors.clientType
                                ? "is-invalid"
                                : ""
                            }
                            placeholder="Select Client Type"
                          />
                          {formik.touched.clientType && formik.errors.clientType && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.clientType}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Client <span className="text-danger">*</span>
                          </Label>
                          {formik.values.clientType?.value === 2 ? (
                            <Input
                              name="clientName"
                              type="text"
                              placeholder="Enter Client Name"
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                              invalid={formik.touched.clientType && !clientName.trim()}
                            />
                          ) : (
                            <Select
                              name="client"
                              options={(clientList || []).map((item) => ({ value: item.client_id, label: item.client_name }))}
                              value={formik.values.client}
                              onChange={(option) => {
                                formik.setFieldValue("client", option);
                                formik.setFieldTouched("client", true);
                              }}
                              className={
                                formik.touched.client && formik.errors.client
                                  ? "is-invalid"
                                  : ""
                              }
                              placeholder="Select Client"
                            />
                          )}
                          {formik.touched.client && formik.errors.client && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.client}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Trip Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="tripName"
                            type="text"
                            placeholder="Enter Trip Name"
                            value={formik.values.tripName}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.tripName && formik.errors.tripName
                            }
                          />
                          {formik.touched.tripName && formik.errors.tripName && (
                            <FormFeedback>{formik.errors.tripName}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Assign To <span className="text-danger">*</span>
                          </Label>
                          <Select
                            name="assignTo"
                            isMulti
                            options={(employees || []).map((item) => ({ value: item.emp_id, label: item.emp_name }))}
                            value={formik.values.assignTo}
                            onChange={(option) => {
                              formik.setFieldValue("assignTo", option || []);
                              formik.setFieldTouched("assignTo", true);
                            }}
                            className={
                              formik.touched.assignTo && formik.errors.assignTo
                                ? "is-invalid"
                                : ""
                            }
                            placeholder="Select Employees"
                          />
                          {formik.touched.assignTo && formik.errors.assignTo && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.assignTo}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            From Date <span className="text-danger">*</span>
                          </Label>
                          <CustomDatePicker
                            name="fromDate"
                            value={formik.values.fromDate}
                            onChange={(date) => formik.setFieldValue("fromDate", date)}
                            onBlur={() => {}}
                            placeholder="Select From Date"
                          />
                          {formik.touched.fromDate && formik.errors.fromDate && (
                            <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              {formik.errors.fromDate}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            To Date <span className="text-danger">*</span>
                          </Label>
                          <CustomDatePicker
                            name="toDate"
                            value={formik.values.toDate}
                            onChange={(date) => formik.setFieldValue("toDate", date)}
                            onBlur={() => {}}
                            placeholder="Select To Date"
                          />
                          {formik.touched.toDate && formik.errors.toDate && (
                            <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              {formik.errors.toDate}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col className="d-flex justify-content-end align-items-center">
                        <Button type="submit" className="btn button-save me-2">
                          <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                        </Button>
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleCancel}
                          className="btn button-cancel"
                        >
                          <i className="fas fa-sync me-1" /> Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {activeTripmaster.length > 0 && (
            <Row>
              <Col lg={12}> 
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={activeTripmaster}
                      isGlobalFilter={true}
                      customPageSize={10}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TripMaster;
